import React, {
  useCallback, useContext, useEffect, useRef, useState, useMemo,
} from 'react';
import * as Sentry from '@sentry/browser';
import PropTypes from 'prop-types';
import { Switch, FormControlLabel } from '@mui/material';
import CheckInTheme from '../../../../../../../Model/CheckInTheme';
import useToast from '../../../../../../hooks/useToast';
import FirebaseContext from '../../../../../../../context/FirebaseContext';
import useLogger from '../../../../../../../hooks/useLogger';
import useSessionStore from '../../../../../../../hooks/useSessionStore';
import { CoachingActivity } from '../../../../../../../utils/log';
import SmartCheckInReply from '../../../../../../Model/SmartCheckInReply';
import ExternalCoachContext from '../../../../../../context/ExternalCoachContext';
import { applySkinToneToText } from '../../../../../../../utils/emoji';
import {
  Container,
  SuggestedResponseHeader,
  SuggestedResponseContent,
  AdditionalQueryTitle,
  AdditionalQueryContent,
  ButtonContainer,
  Button,
  ToggleContainer,
  ButtonGroupContainer,
  StyledCircularProgress,
  GeneratedResponseContainer,
} from './styles';
import texts from './texts.json';

const AISuggestedResponse = ({
  checkInId,
  coachId,
  onInsert,
  onThemeInsert,
}) => {
  const { showToast } = useToast();
  const { logCoachingActivity } = useLogger();
  const { authUser } = useSessionStore();
  const { coachDoc } = useContext(ExternalCoachContext);
  const {
    firebase: { remote },
  } = useContext(FirebaseContext);
  const [includeTheme, setIncludeTheme] = useState(false);
  const [activeTheme, setActiveTheme] = useState(null);
  const [isLoadingTheme, setIsLoadingTheme] = useState(false);
  const [generatedResponse, setGeneratedResponse] = useState('');
  const [generatedResponseCheckInId, setGeneratedResponseCheckInId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const additionalQueryRef = useRef(null);
  const isFirstGenerationRef = useRef(true);

  const emojisSkinTone = coachDoc?.emojisSkinTone;

  const displayedResponse = useMemo(
    () => applySkinToneToText(generatedResponse, emojisSkinTone),
    [generatedResponse, emojisSkinTone],
  );

  const fetchCheckInResponse = useCallback(async () => {
    setIsLoading(true);
    setGeneratedResponse('');
    try {
      const additionalQuery = additionalQueryRef.current?.value || '';
      const isRegeneration = !isFirstGenerationRef.current;

      const response = await remote('smartCheckIn-generate', {
        checkInId,
        coachId,
        additionalQuery,
      });
      const data = await response.json();
      setGeneratedResponse(data.data.response);
      setGeneratedResponseCheckInId(checkInId);

      if (isFirstGenerationRef.current) {
        isFirstGenerationRef.current = false;
      }

      const activity = isRegeneration
        ? CoachingActivity.SMART_CHECKIN_RESPONSE_REGENERATED
        : CoachingActivity.SMART_CHECKIN_RESPONSE_GENERATED;

      logCoachingActivity(activity, {
        checkInId,
        hasAdditionalQuery: !!additionalQuery,
        userId: authUser?.uid,
      });
    } catch (error) {
      showToast(error.message, { error: true });
    }
    setIsLoading(false);
  }, [remote, checkInId, coachId, logCoachingActivity, authUser.uid, showToast]);

  useEffect(() => {
    isFirstGenerationRef.current = true;
    setGeneratedResponse('');
    if (additionalQueryRef.current) {
      additionalQueryRef.current.value = '';
    }
  }, [checkInId]);

  useEffect(() => {
    const fetchSmartCheckInReply = async () => {
      setIsLoading(true);

      try {
        if (checkInId) {
          const smartCheckInRepliesCollection = await SmartCheckInReply
            .getLatestByCheckInIdAndCoach(checkInId, coachId);
          const smartCheckInReplyDoc = smartCheckInRepliesCollection.docs[0];

          if (smartCheckInReplyDoc && smartCheckInReplyDoc.generatedResponse) {
            setGeneratedResponse(smartCheckInReplyDoc.generatedResponse);
            setGeneratedResponseCheckInId(smartCheckInReplyDoc.checkInId);
            logCoachingActivity(CoachingActivity.SMART_CHECKIN_RESPONSE_GENERATED, {
              checkInId,
              hasAdditionalQuery: false,
              userId: authUser?.uid,
            });
          } else {
            await fetchCheckInResponse();
            return;
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchSmartCheckInReply();
  }, [authUser.uid, checkInId, coachId, logCoachingActivity, fetchCheckInResponse]);

  // Auto insert the response if it is the first generation
  useEffect(() => {
    if (displayedResponse && isFirstGenerationRef.current && generatedResponseCheckInId === checkInId) {
      onInsert(displayedResponse, displayedResponse);
    }
  }, [displayedResponse, onInsert, checkInId, generatedResponseCheckInId]);

  const handleToggleChange = useCallback(async (event) => {
    const isChecked = event.target.checked;
    setIncludeTheme(isChecked);

    if (isChecked) {
      setIsLoadingTheme(true);
      try {
        const theme = await CheckInTheme.getThemeForCurrentDate();
        if (!theme) {
          showToast(texts.noActiveTheme, { error: true });
          setIncludeTheme(false);
        }
        setActiveTheme(theme);
        onThemeInsert(theme);
        let finalResponse = displayedResponse;
        if (theme) {
          finalResponse = `${displayedResponse}\n\n${theme.description}`;
        }
        onInsert(finalResponse, displayedResponse, true); // isManualInsert = true for manual button click
      } catch (error) {
        showToast(texts.errorLoadingTheme, { error: true });
        Sentry.captureException(error, {
          extra: {
            userId: authUser?.uid,
            checkInId,
          },
        });
        setActiveTheme(null);
        onInsert(displayedResponse, displayedResponse, true); // isManualInsert = true for manual button click
        setIncludeTheme(false);
      } finally {
        setIsLoadingTheme(false);
      }
    } else {
      setActiveTheme(null);
      onInsert(displayedResponse, displayedResponse, true); // isManualInsert = true for manual button click
    }
  }, [onThemeInsert, showToast, authUser.uid, checkInId, displayedResponse, onInsert]);

  const handleInsert = async () => {
    let finalResponse = displayedResponse;
    if (includeTheme && activeTheme) {
      finalResponse = `${displayedResponse}\n\n${activeTheme.description}`;
    }

    const additionalQuery = additionalQueryRef.current?.value || '';
    logCoachingActivity(CoachingActivity.SMART_CHECKIN_RESPONSE_INSERTED, {
      checkInId,
      hasTheme: includeTheme && !!activeTheme,
      hasAdditionalQuery: !!additionalQuery,
      userId: authUser?.uid,
    });

    onInsert(finalResponse, generatedResponse, true); // isManualInsert = true for manual button click

    try {
      await remote('smartCheckIn-markReplyUsed', {
        checkInId,
        coachId,
      });
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          checkInId,
          action: 'markReplyUsed',
        },
        extra: {
          userId: authUser?.uid,
        },
      });
    }
  };

  return (
    <Container>
      <SuggestedResponseHeader>{texts.title}</SuggestedResponseHeader>
      <SuggestedResponseContent>
        {isLoading ? <StyledCircularProgress />
          : <GeneratedResponseContainer>{displayedResponse}</GeneratedResponseContainer>}
        <AdditionalQueryTitle>{texts.additionalQueryTitle}</AdditionalQueryTitle>
        <AdditionalQueryContent
          placeholder={texts.additionalQueryPlaceholder}
          rows={3}
          maxRows={5}
          minRows={3}
          ref={additionalQueryRef}
        />
        <ButtonContainer>
          <ButtonGroupContainer>
            <Button onClick={handleInsert}>{texts.insertButton}</Button>
            <ToggleContainer>
              <FormControlLabel
                control={(
                  <Switch
                    checked={includeTheme}
                    onChange={handleToggleChange}
                    disabled={isLoadingTheme}
                    size="small"
                  />
                )}
                label={texts.checkInThemeLabel}
              />
              {isLoadingTheme && (
                <StyledCircularProgress size={16} />
              )}
            </ToggleContainer>
          </ButtonGroupContainer>
          <Button onClick={fetchCheckInResponse}>{texts.regenerateButton}</Button>
        </ButtonContainer>
      </SuggestedResponseContent>
    </Container>
  );
};

AISuggestedResponse.propTypes = {
  onInsert: PropTypes.func.isRequired,
  checkInId: PropTypes.string.isRequired,
  coachId: PropTypes.string.isRequired,
  onThemeInsert: PropTypes.func,
};

AISuggestedResponse.defaultProps = {
  onThemeInsert: () => {},
};

export default AISuggestedResponse;
